import {
  getPlayersSearch,
  type LineupEntry,
} from "@aura-frontend/omnibus-az-client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  getLineupJerseyNumber,
  useTeamJerseyNumbers,
} from "@/hooks/useTeamJerseyNumbers";
import { mapLineupEntryToIPLayer } from "@/pages/Tagging/utils/api-helpers";
import { GamePlayer, GameTeam } from "@/types/game";

import type { GameTeams, TeamRef } from "../EventForm";
import { lineupFormSchema, type LineupFormValues } from "../EventForm.schema";
import { PlayerSelectModal } from "./PlayerSelectModal";
import { TeamLineupPanel } from "./TeamLineupPanel";
import { type SelectTeamSelection, TeamPanel } from "./TeamPanel";
import { mapPublicPlayerToGamePlayer } from "./TeamSelectModal";

// Players without an id can't go in a lineup, and would fail the lineup check
const hasPlayerId = (player: GamePlayer) => player.uuid !== "";

const emptyTeamState = (): GameTeam => ({
  name: "",
  uuid: "",
  players: [],
  currentLineup: [],
  currentPitcher: null,
  currentCatcher: null,
});

const lineupEntryToGamePlayer = (entry: LineupEntry): GamePlayer => {
  const {
    position: _position,
    jerseyNumber,
    ...player
  } = mapLineupEntryToIPLayer(entry);
  // Entries saved without sides come back undefined, which the check rejects
  return {
    ...player,
    batHand: player.batHand ?? "R",
    throwHand: player.throwHand ?? "R",
    jerseyNumber: String(jerseyNumber),
  };
};

const hydrateTeamFromLineup = (
  base: GameTeam,
  entries: LineupEntry[] | undefined,
): GameTeam => {
  const saved = entries?.filter((e) => e.externalId);
  if (!saved || saved.length === 0) return base;
  const pitcherEntry = saved.find((e) => e.position === "pitcher");
  const catcherEntry = saved.find((e) => e.position === "catcher");
  const battingEntries = saved
    .filter((e) => typeof e.position === "number")
    .sort((a, b) => (a.position as number) - (b.position as number));
  return {
    ...base,
    currentPitcher: pitcherEntry ? lineupEntryToGamePlayer(pitcherEntry) : null,
    currentCatcher: catcherEntry ? lineupEntryToGamePlayer(catcherEntry) : null,
    currentLineup: battingEntries.map(lineupEntryToGamePlayer),
  };
};

type LineupProps = {
  gameTeams?: GameTeams;
  initialLineups?: { home: LineupEntry[]; away: LineupEntry[] };
  onTeamChange?: (side: TTeamSides, ref: TeamRef) => void;
  onValidChange?: (isValid: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  /** Null while the lineup doesn't validate */
  onValuesChange?: (values: LineupFormValues | null) => void;
  isEditing?: boolean;
};

function Lineup({
  gameTeams,
  initialLineups,
  onTeamChange,
  onValidChange,
  onDirtyChange,
  onValuesChange,
  isEditing = false,
}: LineupProps) {
  const [awayTeam, setAwayTeam] = useState<GameTeam>(() =>
    hydrateTeamFromLineup(
      {
        ...emptyTeamState(),
        uuid: gameTeams?.away.uuid ?? "",
        name: gameTeams?.away.name ?? "",
        parentName: gameTeams?.away.parentName ?? null,
      },
      initialLineups?.away,
    ),
  );
  const [homeTeam, setHomeTeam] = useState<GameTeam>(() =>
    hydrateTeamFromLineup(
      {
        ...emptyTeamState(),
        uuid: gameTeams?.home.uuid ?? "",
        name: gameTeams?.home.name ?? "",
        parentName: gameTeams?.home.parentName ?? null,
      },
      initialLineups?.home,
    ),
  );

  useEffect(() => {
    if (!gameTeams) return;
    if (gameTeams.home.uuid !== homeTeam.uuid) {
      setHomeTeam({
        ...emptyTeamState(),
        uuid: gameTeams.home.uuid,
        name: gameTeams.home.name,
        parentName: gameTeams.home.parentName ?? null,
      });
    } else if (
      (homeTeam.parentName ?? null) !== (gameTeams.home.parentName ?? null)
    ) {
      setHomeTeam((prev) => ({
        ...prev,
        parentName: gameTeams.home.parentName ?? null,
      }));
    }
    if (gameTeams.away.uuid !== awayTeam.uuid) {
      setAwayTeam({
        ...emptyTeamState(),
        uuid: gameTeams.away.uuid,
        name: gameTeams.away.name,
        parentName: gameTeams.away.parentName ?? null,
      });
    } else if (
      (awayTeam.parentName ?? null) !== (gameTeams.away.parentName ?? null)
    ) {
      setAwayTeam((prev) => ({
        ...prev,
        parentName: gameTeams.away.parentName ?? null,
      }));
    }
  }, [
    gameTeams,
    homeTeam.uuid,
    awayTeam.uuid,
    homeTeam.parentName,
    awayTeam.parentName,
  ]);

  const homeQuery = useInfiniteQuery({
    queryKey: [
      "getPlayersSearch",
      "home",
      gameTeams?.home.uuid ?? "",
      gameTeams?.home.visibilityGroup ?? "",
    ],
    queryFn: async ({ pageParam }) => {
      const { data } = await getPlayersSearch({
        query: {
          continue: pageParam || undefined,
          teamId: gameTeams?.home.uuid,
          visibilityGroup: gameTeams!.home.visibilityGroup,
        },
        throwOnError: true,
      });
      return data;
    },
    enabled: !!gameTeams?.home.uuid && !!gameTeams?.home.visibilityGroup,
    getNextPageParam: (lastPage) => lastPage.continue,
    initialPageParam: "",
  });

  const awayQuery = useInfiniteQuery({
    queryKey: [
      "getPlayersSearch",
      "away",
      gameTeams?.away.uuid ?? "",
      gameTeams?.away.visibilityGroup ?? "",
    ],
    queryFn: async ({ pageParam }) => {
      const { data } = await getPlayersSearch({
        query: {
          continue: pageParam || undefined,
          teamId: gameTeams?.away.uuid,
          visibilityGroup: gameTeams!.away.visibilityGroup,
        },
        throwOnError: true,
      });
      return data;
    },
    enabled: !!gameTeams?.away.uuid && !!gameTeams?.away.visibilityGroup,
    getNextPageParam: (lastPage) => lastPage.continue,
    initialPageParam: "",
  });

  const homeJerseys = useTeamJerseyNumbers(gameTeams?.home.uuid);
  const awayJerseys = useTeamJerseyNumbers(gameTeams?.away.uuid);

  const homePlayers = useMemo(
    () =>
      // Held back until the jersey numbers are known, so no player is picked
      // into the lineup without theirs
      homeJerseys.isLoading
        ? []
        : (homeQuery.data?.pages
            .flatMap((page) => page.players ?? [])
            .map((player) =>
              mapPublicPlayerToGamePlayer(
                player,
                getLineupJerseyNumber(homeJerseys.data, player),
              ),
            )
            .filter(hasPlayerId) ?? []),
    [homeQuery.data, homeJerseys.data, homeJerseys.isLoading],
  );

  const awayPlayers = useMemo(
    () =>
      // Held back until the jersey numbers are known, so no player is picked
      // into the lineup without theirs
      awayJerseys.isLoading
        ? []
        : (awayQuery.data?.pages
            .flatMap((page) => page.players ?? [])
            .map((player) =>
              mapPublicPlayerToGamePlayer(
                player,
                getLineupJerseyNumber(awayJerseys.data, player),
              ),
            )
            .filter(hasPlayerId) ?? []),
    [awayQuery.data, awayJerseys.data, awayJerseys.isLoading],
  );

  useEffect(() => {
    setHomeTeam((prev) =>
      prev.players === homePlayers ? prev : { ...prev, players: homePlayers },
    );
  }, [homePlayers]);

  useEffect(() => {
    setAwayTeam((prev) =>
      prev.players === awayPlayers ? prev : { ...prev, players: awayPlayers },
    );
  }, [awayPlayers]);

  const loadMoreHome = () => {
    if (homeQuery.hasNextPage && !homeQuery.isFetchingNextPage) {
      homeQuery.fetchNextPage();
    }
  };

  const loadMoreAway = () => {
    if (awayQuery.hasNextPage && !awayQuery.isFetchingNextPage) {
      awayQuery.fetchNextPage();
    }
  };

  useEffect(() => {
    const parsed = lineupFormSchema.safeParse({ homeTeam, awayTeam });
    onValidChange?.(parsed.success);
    onValuesChange?.(parsed.success ? parsed.data : null);
  }, [homeTeam, awayTeam, onValidChange, onValuesChange]);

  const initialDirtySnapshotRef = useRef<string | null>(null);
  useEffect(() => {
    const signature = (team: GameTeam) =>
      JSON.stringify({
        uuid: team.uuid,
        name: team.name,
        parentName: team.parentName ?? null,
        currentLineup: team.currentLineup,
        currentPitcher: team.currentPitcher,
        currentCatcher: team.currentCatcher,
      });
    const current = `${signature(homeTeam)}|${signature(awayTeam)}`;
    if (initialDirtySnapshotRef.current === null) {
      initialDirtySnapshotRef.current = current;
      onDirtyChange?.(false);
      return;
    }
    onDirtyChange?.(current !== initialDirtySnapshotRef.current);
  }, [homeTeam, awayTeam, onDirtyChange]);

  const [playerSelectModal, setPlayerSelectModal] = useState<{
    isOpen: boolean;
    team: TTeamSides;
    type: "pitcher" | "catcher";
  }>({ isOpen: false, team: "away", type: "pitcher" });

  const getTeam = (side: TTeamSides) => (side === "away" ? awayTeam : homeTeam);

  const setTeam = (side: TTeamSides, update: Partial<GameTeam>) => {
    if (side === "away") {
      setAwayTeam((prev) => ({ ...prev, ...update }));
    } else {
      setHomeTeam((prev) => ({ ...prev, ...update }));
    }
  };

  const handleTeamSelect = (side: TTeamSides, data: SelectTeamSelection) => {
    const current = getTeam(side);
    const isSameTeam = current.uuid === data.teamUuid && !!data.teamUuid;
    setTeam(
      side,
      isSameTeam
        ? {
            name: data.teamName,
            parentName: data.parentName ?? null,
          }
        : {
            name: data.teamName,
            uuid: data.teamUuid,
            parentName: data.parentName ?? null,
            players: [],
            currentLineup: [],
            currentPitcher: null,
            currentCatcher: null,
          },
    );
    onTeamChange?.(side, {
      uuid: data.teamUuid,
      name: data.teamName,
      visibilityGroup: data.visibilityGroup,
      parentName: data.parentName ?? null,
    });
  };

  const handlePlayerSelect = (player: GamePlayer) => {
    const { team, type } = playerSelectModal;
    setTeam(team, {
      [type === "pitcher" ? "currentPitcher" : "currentCatcher"]: player,
    });
    setPlayerSelectModal({ isOpen: false, team: "away", type: "pitcher" });
  };

  const clearTeam = (side: TTeamSides) => {
    setTeam(side, emptyTeamState());
    onTeamChange?.(side, {
      uuid: "",
      name: "",
      visibilityGroup: "",
      parentName: null,
    });
  };

  const makeTeamHandlers = (side: TTeamSides) => ({
    onSelectTeam: (selection: SelectTeamSelection) =>
      handleTeamSelect(side, selection),
    onClearTeam: () => clearTeam(side),
    onSelectPlayer: (type: "pitcher" | "catcher") =>
      setPlayerSelectModal({ isOpen: true, team: side, type }),
    onClearPitcher: () => setTeam(side, { currentPitcher: null }),
    onClearCatcher: () => setTeam(side, { currentCatcher: null }),
    onAddToLineup: (player: GamePlayer) =>
      setTeam(side, {
        currentLineup: [...getTeam(side).currentLineup, player],
      }),
    onRemoveFromLineup: (player: GamePlayer) =>
      setTeam(side, {
        currentLineup: getTeam(side).currentLineup.filter(
          (p) => p.uuid !== player.uuid,
        ),
      }),
    onReorderLineup: (newLineup: GamePlayer[]) =>
      setTeam(side, { currentLineup: newLineup }),
  });

  return (
    <div className="@container flex flex-1 flex-col bg-white">
      <div className="text-base font-bold mb-6 mt-4 text-center">
        {isEditing ? "Lineup" : "Step #3: Lineup"}
      </div>

      <div className="flex flex-1 flex-col @[600px]:flex-row overflow-hidden">
        {awayTeam.name ? (
          <TeamLineupPanel
            side="away"
            team={awayTeam}
            visibilityGroup={gameTeams?.away.visibilityGroup ?? ""}
            hasMorePlayers={awayQuery.hasNextPage}
            isLoadingMorePlayers={awayQuery.isFetchingNextPage}
            isLoadingPlayers={
              (awayQuery.isFetching && !awayQuery.isFetchingNextPage) ||
              awayJerseys.isLoading
            }
            onLoadMorePlayers={loadMoreAway}
            {...makeTeamHandlers("away")}
          />
        ) : (
          <TeamPanel
            side="away"
            team={awayTeam}
            onSelectTeam={(selection) => handleTeamSelect("away", selection)}
          />
        )}

        <div className="h-px w-auto @[600px]:h-auto @[600px]:w-px bg-gray-200" />

        {homeTeam.name ? (
          <TeamLineupPanel
            side="home"
            team={homeTeam}
            visibilityGroup={gameTeams?.home.visibilityGroup ?? ""}
            hasMorePlayers={homeQuery.hasNextPage}
            isLoadingMorePlayers={homeQuery.isFetchingNextPage}
            isLoadingPlayers={
              (homeQuery.isFetching && !homeQuery.isFetchingNextPage) ||
              homeJerseys.isLoading
            }
            onLoadMorePlayers={loadMoreHome}
            {...makeTeamHandlers("home")}
          />
        ) : (
          <TeamPanel
            side="home"
            team={homeTeam}
            onSelectTeam={(selection) => handleTeamSelect("home", selection)}
          />
        )}
      </div>

      <PlayerSelectModal
        isOpen={playerSelectModal.isOpen}
        onClose={() =>
          setPlayerSelectModal({ isOpen: false, team: "away", type: "pitcher" })
        }
        onSubmit={handlePlayerSelect}
        roster={getTeam(playerSelectModal.team).players}
        team={playerSelectModal.team}
        type={playerSelectModal.type}
      />
    </div>
  );
}

export { Lineup };
